import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { TABLES } from '../../../../Apis/Tables';
import TableFunction from '../../../../components/table/TableFunction';
import { SEARCH_MODEL } from '../../../../components/table/Model/TableModel';
import { VIEW_TYPE } from '../../../../enums/ViewType';
import { SimpleSearch } from '../../../../reduxToolkit/Search/SearchApi';
import { LAYOUT_MODEL, RENAME_LAYOUT_MODEL } from '../Model/Model';

/**
 * @Author      Karen Yunuen Pozos Arias
 * @JobTitle    Full Stack
 * @Email       kpozos@ibl.mx
 *
 * Saved Layouts tab: page of the layouts of the admin, searched by name with the simple search, and the
 * inline rename of a layout. The new name is applied when it is confirmed.
 */
const SavedLayoutsTabApp = ({ modified, setModified, onRename }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [searchElement, setSearchElement] = useState('');
    const [results, setResults] = useState({ content: [], totalElements: 0 });
    const [editing, setEditing] = useState(RENAME_LAYOUT_MODEL.clear());
    // Search key the page was read with, the debounce of CustomSearch also runs on mount and
    // that first page is already brought by TableFunction //
    const requestedSearchElement = useRef('');

    /**
     * Reads a page of the saved layouts with the EVENT_MONITORING_LAYOUT model of the simple search.
     */
    const getData = (page, options) => {
        const key = options?.searchElement ?? searchElement;
        requestedSearchElement.current = key;

        const body = SEARCH_MODEL.setObject({
            page,
            size: options?.rowsPerPage || rowsPerPage,
            model: TABLES.EVENT_MONITORING_LAYOUT,
            key,
        });

        dispatch(SimpleSearch({ body })).then(({ payload: { data } }) => {
            if (!data?.success) return;
            setResults({ ...data.data, content: (data.data?.content ?? []).map(LAYOUT_MODEL.fromApi) });
        });
    };

    const { pagination, rowsPerPage } = TableFunction({
        data: results,
        labelRowsPerPage: 'layouts_per_page',
        hasContainer: true,
        fixedPagination: false,
        view: VIEW_TYPE.LIST.name,
        searchElement,
        getData,
        modified,
        setModified
    });

    /**
     * Search of CustomSearch, it runs debounced on every change and reads the first page with the key typed.
     */
    const handleSearch = (value) => {
        if (requestedSearchElement.current === value) return;
        setModified(true);
    };

    const handleResetSearch = () => {
        setSearchElement('');
        setModified(true);
    };

    const startEditing = (layout) => setEditing({ id: layout.id, name: layout.name });

    const cancelEditing = () => setEditing(RENAME_LAYOUT_MODEL.clear());

    const handleChangeName = (e) => setEditing((prev) => ({ ...prev, name: e.target.value }));

    const saveName = () => {
        if (!RENAME_LAYOUT_MODEL.isValidName(editing.name)) return;
        onRename(editing.id, editing.name);
        cancelEditing();
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') saveName();
        if (e.key === 'Escape') cancelEditing();
    };

    return {
        t,
        layouts: results.content ?? [],
        searchElement,
        setSearchElement,
        handleSearch,
        handleResetSearch,
        pagination,
        editing,
        startEditing,
        handleChangeName,
        saveName,
        handleKeyDown,
        isValidName: RENAME_LAYOUT_MODEL.isValidName(editing.name)
    };
};

export default SavedLayoutsTabApp;
